/**
 * TaskCalendarWidget — Calendar view of tasks for subject (DRR-321)
 *
 * Shows a mini calendar with task indicators for the current week/month.
 * Complements the TasksPanel list view with a time-based perspective.
 */

import React, { useState, useMemo } from 'react';
import {
  Stack,
  Text,
  Badge,
  Group,
  Skeleton,
  Alert,
  Paper,
  Box,
  SimpleGrid,
  ActionIcon,
  Indicator,
  Tooltip,
} from '@mantine/core';
import {
  IconCalendar,
  IconAlertCircle,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';
import { useGetTasksBySubjectQuery, GqlTaskStatus } from '@/generated/graphql';
import type { WidgetProps } from '@ferp/core';

type CalendarTask = {
  id: string;
  name: string;
  status: GqlTaskStatus;
  priority?: string | null;
  dueAt?: string | null;
  scheduledStart?: string | null;
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getDaysInMonth(year: number, month: number): Date[] {
  const days: Date[] = [];
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // Add days from previous month to fill first week
  const startPadding = firstDay.getDay();
  for (let i = startPadding - 1; i >= 0; i--) {
    days.push(new Date(year, month, -i));
  }

  // Add days of current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push(new Date(year, month, i));
  }

  // Add days from next month to fill last week
  const endPadding = 6 - lastDay.getDay();
  for (let i = 1; i <= endPadding; i++) {
    days.push(new Date(year, month + 1, i));
  }

  return days;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

export function TaskCalendarWidget({ subjectActorId }: WidgetProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const today = useMemo(() => new Date(), []);

  const { data, loading, error } = useGetTasksBySubjectQuery({
    variables: { subjectActorId: subjectActorId ?? '' },
    skip: !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  const tasks = useMemo((): CalendarTask[] => {
    const rawTasks = data?.taskApiGetTasksBySubject ?? [];
    return rawTasks.map(t => ({
      id: t.id,
      name: t.name,
      status: t.status,
      priority: t.priority,
      dueAt: t.dueAt,
      scheduledStart: t.scheduledStart,
    }));
  }, [data]);

  const tasksByDate = useMemo(() => {
    const map = new Map<string, CalendarTask[]>();
    for (const task of tasks) {
      const dateStr = task.dueAt ?? task.scheduledStart;
      if (!dateStr) continue;
      const date = new Date(dateStr);
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(task);
    }
    return map;
  }, [tasks]);

  const days = useMemo(() => {
    return getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth());
  }, [currentDate]);

  const getTasksForDay = (date: Date): CalendarTask[] => {
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    return tasksByDate.get(key) ?? [];
  };

  const getDayIndicatorColor = (dayTasks: CalendarTask[]): string | undefined => {
    if (dayTasks.length === 0) return undefined;
    if (dayTasks.some(t => t.priority === 'CRITICAL')) return 'red';
    if (dayTasks.some(t => t.priority === 'HIGH')) return 'orange';
    if (dayTasks.some(t => t.status === GqlTaskStatus.PENDING || t.status === GqlTaskStatus.ASSIGNED)) return 'yellow';
    return 'blue';
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  if (loading && tasks.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={30} />
        <Skeleton height={150} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Error loading calendar.
      </Alert>
    );
  }

  const monthStr = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <Stack gap="sm">
      <Group gap="xs" justify="space-between">
        <Group gap="xs">
          <IconCalendar size={16} color="var(--mantine-color-blue-6)" />
          <Text fw={600} size="sm">Task Calendar</Text>
        </Group>
        <Badge size="sm" variant="light">{tasks.length} tasks</Badge>
      </Group>

      <Paper withBorder p="xs">
        <Group justify="space-between" mb="xs">
          <ActionIcon variant="subtle" size="sm" onClick={prevMonth}>
            <IconChevronLeft size={14} />
          </ActionIcon>
          <Text fw={500} size="sm">{monthStr}</Text>
          <ActionIcon variant="subtle" size="sm" onClick={nextMonth}>
            <IconChevronRight size={14} />
          </ActionIcon>
        </Group>

        <SimpleGrid cols={7} spacing={2}>
          {WEEKDAYS.map(day => (
            <Text key={day} size="xs" c="dimmed" ta="center" fw={500}>
              {day}
            </Text>
          ))}

          {days.map((date, i) => {
            const dayTasks = getTasksForDay(date);
            const isCurrentMonth = date.getMonth() === currentDate.getMonth();
            const isToday = isSameDay(date, today);
            const indicatorColor = getDayIndicatorColor(dayTasks);

            return (
              <Tooltip
                key={i}
                label={dayTasks.length > 0 ? `${dayTasks.length} task${dayTasks.length !== 1 ? 's' : ''}` : undefined}
                disabled={dayTasks.length === 0}
              >
                <Box
                  p={4}
                  style={{
                    textAlign: 'center',
                    borderRadius: 4,
                    backgroundColor: isToday ? 'var(--mantine-color-blue-1)' : undefined,
                    opacity: isCurrentMonth ? 1 : 0.4,
                    cursor: dayTasks.length > 0 ? 'pointer' : undefined,
                  }}
                >
                  <Indicator
                    color={indicatorColor}
                    size={6}
                    offset={2}
                    disabled={!indicatorColor}
                  >
                    <Text
                      size="xs"
                      fw={isToday ? 700 : 400}
                      c={isToday ? 'blue' : undefined}
                    >
                      {date.getDate()}
                    </Text>
                  </Indicator>
                </Box>
              </Tooltip>
            );
          })}
        </SimpleGrid>
      </Paper>

      {tasks.length === 0 && (
        <Alert icon={<IconCalendar size={16} />} color="blue" variant="light">
          No scheduled tasks for this subject.
        </Alert>
      )}
    </Stack>
  );
}
